import React from 'react';
import { motion } from 'framer-motion';
import { InlineWidget } from 'react-calendly';
import {
    Calendar,
    Clock,
    Users,
    MessageCircle,
    Phone,
    Mail
} from 'lucide-react';

const ScheduleCall = () => {
    const callInfo = [
        {
            icon: Calendar,
            title: "Schedule Your Call",
            details: [
                "Choose from available time slots",
                "Flexible scheduling options"
            ]
        },
        {
            icon: Clock,
            title: "Call Duration",
            details: [
                "30/45-minute consultation",
                "Focused discussion time"
            ]
        },
        {
            icon: Users,
            title: "Expert Team",
            details: [
                "Experience Global Executives",
                "Top Software Engineer, Architect, and DevOps",
                "Healthcare professionals",
                "Education specialists"
            ]
        },
        {
            icon: MessageCircle,
            title: "Discussion Topics",
            details: [
                "Investment opportunities",
                "School enrollment opportunities"
            ]
        }
    ];

    const supportChannels = [
        {
            icon: MessageCircle,
            title: "WhatsApp Support",
            description: "Quick responses via WhatsApp",
            contact: "+220 404 0966",
            availability: "24/7"
        },
        {
            icon: Mail,
            title: "Email Support",
            description: "Detailed inquiries and documentation",
            contact: "info@amhas.org",
            availability: "Response within 24 hours"
        },
        {
            icon: Phone,
            title: "Phone Support",
            description: "Direct phone support for urgent matters",
            contact: "+220 404 0966",
            availability: "Mon-Fri: 8AM-5PM"
        }
    ];

    const callTypes = [
        {
            name: "School Partnerships",
            description: "30 min • Google Meet • One-on-One",
            contact: "Weekdays, 9 am - 5 pm",
            url: "https://calendly.com/amhas-info/school-partnerships"
        },
        {
            name: "General Information",
            description: "30 min • Google Meet • One-on-One",
            contact: "Weekdays, 9 am - 5 pm",
            url: "https://calendly.com/amhas-info/general-information"
        },
        {
            name: "Investor Meet & Greet",
            description: "45 min • Google Meet • One-on-One",
            contact: "Weekdays, 9 am - 5 pm",
            url: "https://calendly.com/amhas-info/30min"
        }
    ];

    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <h1 className="text-5xl md:text-6xl font-bold mb-6">Schedule a Call</h1>
                        <p className="text-xl md:text-2xl text-primary-100">
                            Book a consultation with our team
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Call Information */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="section-title">Schedule Your Consultation</h2>
                        <p className="section-subtitle">
                            Multiple ways to connect with us
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {callInfo.map((info, index) => {
                            const Icon = info.icon;
                            return (
                                <motion.div
                                    key={info.title}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="card p-6 text-center"
                                >
                                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Icon size={32} className="text-primary-600" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{info.title}</h3>
                                    <div className="space-y-1">
                                        {info.details.map((detail, detailIndex) => (
                                            <p key={detailIndex} className="text-gray-600 text-sm">{detail}</p>
                                        ))}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Calendly Integration */}
            <section className="section-padding bg-gray-50">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="max-w-4xl mx-auto"
                    >
                        <div className="text-center mb-12">
                            <h2 className="section-title">Book Your Call</h2>
                            <p className="section-subtitle">
                                Select a convenient time for your consultation
                            </p>
                        </div>

                        <div className="card p-8">
                            <div className="text-center mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">Investor Introductions</h3>
                                <p className="text-gray-600 mb-6">
                                    Schedule a 30-minute call to discuss investment opportunities and partnership possibilities with AMHAS.
                                </p>
                                <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
                                    <div className="flex items-center gap-2">
                                        <Clock size={16} />
                                        <span>30 minutes</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar size={16} />
                                        <span>Flexible scheduling</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Users size={16} />
                                        <span>Expert consultation</span>
                                    </div>
                                </div>
                            </div>

                            {/* Calendly Widget */}
                            <InlineWidget
                                url="https://calendly.com/amhas-info"
                                styles={{ minWidth: "320px", height: "700px" }}
                            />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Support Channels */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="section-title">Support Channels</h2>
                        <p className="section-subtitle">
                            Choose the best way to get help
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {supportChannels.map((channel, index) => {
                            const Icon = channel.icon;
                            return (
                                <motion.div
                                    key={channel.title}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="card p-8 text-center"
                                >
                                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <Icon size={32} className="text-primary-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">{channel.title}</h3>
                                    <p className="text-gray-600 mb-4">{channel.description}</p>
                                    <div className="text-primary-600 font-semibold mb-2">{channel.contact}</div>
                                    <div className="text-sm text-gray-500">{channel.availability}</div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Call Types */}
            <section className="section-padding bg-gray-50">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="section-title">Types of Calls</h2>
                        <p className="section-subtitle">
                            Choose your consultation type
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {callTypes.map((type, index) => (
                            <motion.div
                                key={type.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="card p-6 text-center hover:shadow-xl transition-all duration-300"
                            >
                                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Calendar size={32} className="text-primary-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{type.name}</h3>
                                <p className="text-gray-600 text-sm mb-3">{type.description}</p>
                                <div className="text-gray-500 font-semibold text-sm mb-4">{type.contact}</div>
                                <a
                                    href={type.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-primary text-sm px-4 py-2 inline-block"
                                >
                                    Schedule Call
                                </a>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ScheduleCall;